/**
 * v2 feature flags. The scope lives in ONE place, `V2_FEATURES` in
 * shared/constants/features.ts, which the server reads too (it mounts a
 * feature's routes only while its switch is on), so the halves cannot drift.
 *
 * Normal builds: a feature is on when its V2_FEATURES switch is on AND it is
 * in LIVE_FEATURES (it has a real backend). A switch flipped on for a feature
 * with no server routes would otherwise ship pages that 404 on every request.
 *
 * Demo mode (VITE_DEMO_MODE): VITE_SIM_FEATURES picks the set — unset means the
 * agreed scope (V2_FEATURES), "all" turns every feature on for concept demos,
 * a comma list turns on just those, and an empty string turns all of them off
 * (the app then looks and behaves exactly like the core product). The Sim
 * Panel can still flip any feature at runtime; overrides live in the sim store.
 *
 * Market: `seasonal` is Philippines-only and never shows in the main market,
 * whatever its switch says (see isFeatureEnabled).
 */
import { V2_FEATURES, type V2FeatureId } from "@shared/constants/features";
import { getSimState, setSimState, useSimStore, type SimState } from "./store";

export type SimFeatureId = V2FeatureId;

/**
 * Load order, which is also mock precedence: welcome comes first because it
 * answers the pinned-post query on community's path.
 */
export const SIM_FEATURE_IDS: readonly SimFeatureId[] = [
  "welcome",
  "shop",
  "community",
  "safeSpace",
  "stories",
  "registry",
  "dailyWins",
  "seasonal",
  "partners",
  "directory",
];

/**
 * Features with a real backend. A feature joins this set in the same commit
 * that mounts its server routes; until then a normal build leaves it off even
 * when its V2_FEATURES switch is on.
 */
const LIVE_FEATURES: ReadonlySet<SimFeatureId> = new Set<SimFeatureId>([
  "welcome", // server/src/routes/onboarding.routes.ts
  "community", // server/src/routes/community.routes.ts (+ admin Moderation/Badges)
  "safeSpace", // server/src/routes/safeSpace.routes.ts
  "stories", // server/src/routes/stories.routes.ts (+ admin Stories)
  "registry", // server/src/routes/registries.routes.ts + publicRegistry.routes.ts
  "seasonal", // server/src/routes/seasonal.routes.ts (PH accounts only)
]);

/** Features only one market sees. */
const MARKET_ONLY: Partial<Record<SimFeatureId, "main" | "ph">> = { seasonal: "ph" };

/**
 * Features that only make sense on top of another: safe space is the
 * community's settings, promises and support card, so with the community off it
 * goes too (otherwise /settings/community would keep a dead "Back to
 * community" link). The server mounts safeSpaceRoutes on the same rule.
 */
const REQUIRES: Partial<Record<SimFeatureId, SimFeatureId>> = { safeSpace: "community" };

export const SIM_DEMO = Boolean(import.meta.env.VITE_DEMO_MODE);

const IN_SCOPE = SIM_FEATURE_IDS.filter((id) => V2_FEATURES[id]);

function readEnvFeatures(): ReadonlySet<SimFeatureId> {
  if (!SIM_DEMO) return new Set(IN_SCOPE.filter((id) => LIVE_FEATURES.has(id)));
  const raw = import.meta.env.VITE_SIM_FEATURES as string | undefined;
  if (raw === undefined) return new Set(IN_SCOPE);
  if (raw.trim().toLowerCase() === "all") return new Set(SIM_FEATURE_IDS);
  const wanted = new Set(raw.split(",").map((s) => s.trim()));
  return new Set(SIM_FEATURE_IDS.filter((id) => wanted.has(id)));
}

/** The features this build turns on before any Sim Panel override. */
export const ENV_FEATURES = readEnvFeatures();

/**
 * True when at least one feature is on — otherwise the kit renders nothing at
 * all. In a normal build that means "some v2 feature is live"; the demo-only
 * parts (Sim Panel, mock router, persisted store) also check SIM_DEMO.
 */
export const SIM_ACTIVE = ENV_FEATURES.size > 0;

/** Demo-mode extras: the Sim Panel and the feature mocks. Never in a normal build. */
export const SIM_TOOLS = SIM_DEMO && SIM_ACTIVE;

export function isFeatureEnabled(id: SimFeatureId, state: SimState = getSimState()): boolean {
  if (!SIM_ACTIVE) return false;
  const market = MARKET_ONLY[id];
  if (market && state.context.market !== market) return false;
  const base = REQUIRES[id];
  if (base && !isFeatureEnabled(base, state)) return false;
  // Overrides are a demo affordance; a normal build never reads them.
  const override = SIM_DEMO ? state.featureOverrides[id] : undefined;
  return override ?? ENV_FEATURES.has(id);
}

/** Reactive version of isFeatureEnabled (re-renders on Sim Panel toggles and market changes). */
export function useFeatureEnabled(id: SimFeatureId): boolean {
  return useSimStore((s) => isFeatureEnabled(id, s));
}

export function setFeatureOverride(id: SimFeatureId, enabled: boolean): void {
  setSimState((s) => ({ ...s, featureOverrides: { ...s.featureOverrides, [id]: enabled } }));
}
